'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface ServiceItem {
  icon: string;
  titleEs: string;
  titleFr: string;
  descriptionEs: string;
  descriptionFr: string;
  href: string;
  number: string;
}

const services: ServiceItem[] = [
  {
    icon: '01',
    titleEs: 'Asesoría y Consultoría',
    titleFr: 'Conseil et Consulting',
    descriptionEs: 'Planificación digital efectiva para transformar tu organización con estrategia y precisión.',
    descriptionFr: 'Planification numérique efficace pour transformer votre organisation avec stratégie et précision.',
    href: '/servicios/asesoria-consultoria',
    number: '01',
  },
  {
    icon: '02',
    titleEs: 'Ciberseguridad',
    titleFr: 'Cybersécurité',
    descriptionEs: 'Protección integral y cumplimiento normativo para mantener tus datos y sistemas seguros.',
    descriptionFr: 'Protection intégrale et conformité réglementaire pour sécuriser vos données et systèmes.',
    href: '/servicios/ciberseguridad',
    number: '02',
  },
  {
    icon: '03',
    titleEs: 'Soporte Mesa de Ayuda',
    titleFr: 'Support Helpdesk',
    descriptionEs: 'Asistencia técnica continua y soporte TI para mantener tu operación sin interrupciones.',
    descriptionFr: 'Assistance technique continue et support IT pour maintenir votre opération sans interruptions.',
    href: '/servicios/soporte-mesa-ayuda',
    number: '03',
  },
  {
    icon: '04',
    titleEs: 'Desarrollo de Software',
    titleFr: 'Développement Logiciel',
    descriptionEs: 'Soluciones a medida: aplicaciones web, móviles y sistemas empresariales de alto rendimiento.',
    descriptionFr: 'Solutions sur mesure: applications web, mobiles et systèmes d\'entreprise haute performance.',
    href: '/servicios/desarrollo-software',
    number: '04',
  },
  {
    icon: '05',
    titleEs: 'Infraestructura y Redes',
    titleFr: 'Infrastructure et Réseaux',
    descriptionEs: 'Conectividad segura y escalable: servidores, redes y cloud para empresas modernas.',
    descriptionFr: 'Connectivité sécurisée et évolutive: serveurs, réseaux et cloud pour entreprises modernes.',
    href: '/servicios/infraestructura-redes',
    number: '05',
  },
  {
    icon: '06',
    titleEs: 'Capacitaciones',
    titleFr: 'Formations',
    descriptionEs: 'Formación especializada para potenciar las capacidades tecnológicas de tu equipo.',
    descriptionFr: 'Formation spécialisée pour renforcer les capacités technologiques de votre équipe.',
    href: '/servicios/capacitaciones',
    number: '06',
  },
  {
    icon: '07',
    titleEs: 'Equipamiento Tecnológico',
    titleFr: 'Équipement Technologique',
    descriptionEs: 'Venta y provisión de equipamiento tecnológico de las mejores marcas del mercado.',
    descriptionFr: 'Vente et fourniture d\'équipements technologiques des meilleures marques du marché.',
    href: '/servicios/equipamiento-tecnologico',
    number: '07',
  },
  {
    icon: '08',
    titleEs: 'Servicios de Impresión',
    titleFr: 'Services d\'Impression',
    descriptionEs: 'Gestión eficiente de documentos e impresión empresarial con soluciones optimizadas.',
    descriptionFr: 'Gestion efficace des documents et impression d\'entreprise avec des solutions optimisées.',
    href: '/servicios/servicios-impresion',
    number: '08',
  },
  {
    icon: '09',
    titleEs: 'Reciclaje Electrónico',
    titleFr: 'Recyclage Électronique',
    descriptionEs: 'Eliminación responsable de residuos electrónicos con certificación y trazabilidad.',
    descriptionFr: 'Élimination responsable des déchets électroniques avec certification et traçabilité.',
    href: '/servicios/reciclaje-electronico',
    number: '09',
  },
];

const ServicesSection: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const h = t.home;
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionTitle = language === 'fr' ? 'Nos Services' : 'Nuestros Servicios';
  const sectionSubtitle = language === 'fr' ?'Solutions technologiques intégrales pour votre entreprise' :'Soluciones tecnológicas integrales para tu empresa';
  const verMas = language === 'fr' ? 'Voir plus' : 'Ver más';

  return (
    <section
      id="servicios"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#202427', paddingTop: '120px', paddingBottom: '120px' }}>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        <div className="absolute left-0 right-0 top-0 h-px bg-white/6" />
        <div className="absolute left-0 right-0 bottom-0 h-px bg-white/6" />
      </div>

      {/* Accent glow */}
      <div
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(171,210,51,0.05) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className={`flex flex-col sm:flex-row sm:items-end sm:justify-between mb-16 gap-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-px bg-[#abd233]" />
              <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                {sectionTitle}
              </span>
            </div>
            <h2
              className="text-white"
              style={{
                fontSize: 'clamp(28px, 3vw, 48px)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
              }}>
              {sectionSubtitle}
            </h2>
          </div>
          <a
            href="/contact"
            className="group relative inline-flex items-center overflow-hidden flex-shrink-0"
            style={{
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '11px 22px',
              fontSize: '13px',
              color: '#ffffff',
              letterSpacing: '0.02em',
              transition: 'all 300ms ease-out',
            }}>
            <span
              className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
              style={{ zIndex: 0 }}
            />
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
              {language === 'fr' ? 'Nous Contacter' : 'Contáctenos'}
            </span>
          </a>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
          {services.map((service, index) => {
            const title = language === 'fr' ? service.titleFr : service.titleEs;
            const description = language === 'fr' ? service.descriptionFr : service.descriptionEs;
            return (
              <a
                key={index}
                href={service.href}
                className="group relative block p-8 border border-white/6 transition-all duration-300 hover:border-[#abd233]/30 hover:bg-white/2"
                style={{
                  transitionDelay: `${index * 50}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 0.6s ease ${index * 60}ms, transform 0.6s ease ${index * 60}ms, border-color 0.3s, background 0.3s`,
                }}>
                {/* Number */}
                <div
                  className="text-white/10 group-hover:text-[#abd233]/30 transition-colors duration-300 mb-6"
                  style={{ fontSize: '11px', letterSpacing: '0.1em', fontFamily: 'monospace' }}>
                  {service.number}
                </div>

                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-px bg-[#abd233] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

                <h3
                  className="text-white mb-3 group-hover:text-[#abd233] transition-colors duration-300"
                  style={{ fontSize: '18px', fontWeight: 400, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                  {title}
                </h3>

                <p className="text-white/40 group-hover:text-white/60 transition-colors duration-300" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                  {description}
                </p>

                {/* Arrow */}
                <div className="mt-6 flex items-center gap-2 text-[#abd233] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span style={{ fontSize: '12px', letterSpacing: '0.05em' }}>{verMas}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1 6h10M7 2l4 4-4 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;